import zoomSdk from '@zoom/appssdk';

// Capabilities Phase 1 actually uses. Keep this list minimal — Zoom rejects
// the whole config() call with `code:80004 app_not_support` if any entry
// isn't a valid capability for the current context.
const CAPABILITIES = [
  'getRunningContext',
  'getMeetingContext',
  'getUserContext',
  'getSupportedJsApis',
  'authorize',
  'onAuthorized',
  'startRTMS',
  'stopRTMS',
  'onRTMSStatusChange',
  // Zoom carries an open app's webview between the main client and the
  // in-meeting panel; this event is the only way to notice the move and
  // swap dashboard ↔ sidebar. (Must also be enabled in Marketplace →
  // Features → Zoom App SDK.)
  'onRunningContextChange',
  // External links: anchor target=_blank is silently IGNORED inside the
  // Zoom webview — openUrl is the only way out (dead Start-as-host
  // button, 2026-08-13). Must also be enabled in Marketplace → Features.
  'openUrl',
] as const;

let configPromise: ReturnType<typeof zoomSdk.config> | null = null;
let openUrlSupported = true;

/**
 * Zoom requires `config()` to be called exactly once per app load; calling it
 * again returns a stale handshake. Cache the promise so every consumer awaits
 * the same initialization.
 *
 * openUrl is requested opportunistically: if the Marketplace app doesn't
 * have it enabled, Zoom rejects the WHOLE config (80004) — retry without
 * it so a missing checkbox can never brick auth/RTMS.
 */
export function initZoom() {
  if (!configPromise) {
    configPromise = zoomSdk
      .config({ version: '0.16', capabilities: [...CAPABILITIES] })
      .catch((err) => {
        console.warn('[zoom] config with openUrl failed, retrying without', err);
        openUrlSupported = false;
        return zoomSdk.config({
          version: '0.16',
          capabilities: CAPABILITIES.filter((c) => c !== 'openUrl'),
        });
      })
      .catch((err) => {
        console.error('[zoom] config failed', err);
        throw err;
      });
  }
  return configPromise;
}

export { zoomSdk };

/**
 * Ask the Zoom client to start RTMS on the current meeting. The SDK promise
 * resolves once the client has initiated the stream; Zoom will then fire the
 * `meeting.rtms_started` webhook to our backend, which drives the DO pipeline.
 */
export async function startRtms(): Promise<void> {
  await initZoom();
  await zoomSdk.startRTMS();
}

export async function stopRtms(): Promise<void> {
  await initZoom();
  try {
    await zoomSdk.stopRTMS();
  } catch {
    /* If RTMS isn't active the SDK may reject — harmless */
  }
}

type RtmsStatusHandler = Parameters<typeof zoomSdk.onRTMSStatusChange>[0];

// Diagnostic: backend-reported RTMS status (success + failure reasons that
// `startRTMS()`'s bare promise hides). Wire this once at app mount so we can
// see *why* Zoom refuses a stream when the toast "failed to access meeting
// content" fires.
export async function subscribeRtmsStatus(handler: RtmsStatusHandler): Promise<void> {
  await initZoom();
  zoomSdk.onRTMSStatusChange(handler);
}


/**
 * Open an external URL from wherever the app is running: Zoom webview →
 * zoomSdk.openUrl (anchors are silently ignored there); plain browser →
 * window.open fallback.
 */
export async function openExternalUrl(url: string): Promise<'opened' | 'copied' | 'failed'> {
  try {
    await initZoom();
    if (openUrlSupported) {
      await zoomSdk.openUrl({ url });
      return 'opened';
    }
  } catch {
    // fall through to browser/clipboard paths
  }
  const win = window.open(url, '_blank', 'noopener');
  if (win) return 'opened';
  // Zoom webview without openUrl enabled: last resort, put the link on the
  // clipboard so the mediator can paste it into a browser.
  try {
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    return 'failed';
  }
}
